import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Slide,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { type FieldErrors, type FieldPath, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import FileUploadProgress from "@/components/forms/inputs/fileUploadProgress";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import RoundedContentBox from "@/components/general/roundedContentBox";
import ImageViewer from "@/components/general/imageViewer";
import GenericNotification from "@/components/notification/genericNotification";
import { RootState } from "@/components/state/store";
import LmsEnrolmentCreateModal from "@/components/lms/lmsEnrolmentCreateModal";
import LmsBackButton from "@/components/lms/lmsBackButton";
import LmsCourseOverviewHeader from "@/components/lms/lmsCourseOverviewHeader";
import LmsCourseOverviewDangerZone from "@/components/lms/lmsCourseOverviewDangerZone";
import LmsCourseOverviewTabPanels from "@/components/lms/lmsCourseOverviewTabPanels";
import type { LmsCourseOverviewTab } from "@/components/lms/lmsCourseOverviewTypes";
import LmsCourseContentSection from "@/components/lms/lmsCourseContentSection";
import LmsCourseSharedSettingsForm from "@/components/lms/lmsCourseSharedSettingsForm";
import LmsCourseStatusDropDown, {
  type LmsCourseStatusOption,
} from "@/components/lms/lmsCourseStatusDropDown";
import LmsCourseVariantsSection from "@/components/lms/lmsCourseVariantsSection";
import useConfirmOnLeave from "@/helper/customHooks/useConfirmOnLeave";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFileUploadProgress from "@/helper/customHooks/useFileUploadProgress";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";
import { getDownloadUrl, getImageUrl } from "@/helper/imageUrlHandler";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import styles from "@/routes/lms/lmsCourseOverview.module.css";
import LmsFileDropzone from "@/components/lms/lmsFileDropzone";
import type { LmsCourseOverviewLocationState } from "@/routes/lms/lmsCourseCreationFeedback";
import LmsCourseOverviewReadOnly from "@/routes/lms/lmsCourseOverviewReadOnly";
import {
  buildLmsCourseSharedSubmitData,
  buildLmsCourseVariantSubmitData,
  countLmsCourseEditorDirtyFields,
  COURSE_DELETE_PERMISSION,
  COURSE_FEATURED_IMAGE_UPLOAD_PERMISSION,
  COURSE_SCORM_UPLOAD_PERMISSION,
  COURSE_UPDATE_PERMISSION,
  COURSE_VARIANT_UPDATE_PERMISSION,
  createPromiseRequest,
  formatDateValue,
  getEffectiveLmsMode,
  getLmsCourseFeaturedImage,
  getLmsCourseImageViewLink,
  getLmsCoursesRoute,
  getLmsManagerRequestConfig,
  getLmsManagerRequestParameters,
  getDirtyLmsCourseVariantIndexes,
  isLmsCourseSharedSettingsDirty,
  isLmsCourseEnrolmentEligible,
  hasLmsCourseVariantScormPackage,
  LMS_COURSE_SHARED_FIELD_NAMES,
  loadLmsCourseCategoryOptions,
  mapCourseToCourseContentValues,
  mapCourseToCourseEditorFormValues,
  mapCourseToSharedCourseFormValues,
  LmsCourseContentValues,
  LmsCourseEditorFormValues,
  LmsCourseLanguageVariant,
  LmsCourseSharedSettingsFormValues,
  LmsDropDownOption,
  LmsMode,
  shouldBlockLmsForSubdomain,
} from "@/components/lms/lms.utils";

interface Props {
  requiredMode?: LmsMode;
}

type DropDownOption = LmsDropDownOption;
type CourseSettingsFormValues = LmsCourseEditorFormValues;
type CourseEditorValidationField = FieldPath<LmsCourseEditorFormValues>;
type CourseUploadValidationTarget =
  | "replacementScormFile"
  | "replacementImageFile";

const getSharedCourseSettings = (
  values: LmsCourseEditorFormValues
): LmsCourseSharedSettingsFormValues => ({
  isActive: values.isActive,
  category: values.category,
  showCourseDurationOnCertificate: values.showCourseDurationOnCertificate,
  certificateExpirable: values.certificateExpirable,
  isPrivate: values.isPrivate,
  completionIntervalInDays: values.completionIntervalInDays,
  renewalCooldownInDays: values.renewalCooldownInDays,
  validityIntervalInDays: values.validityIntervalInDays,
});

export default function LmsCourseOverview({ requiredMode }: Props) {
  const { t } = useTranslation();
  const settingsFormId = useId();
  const navigate = useNavigate();
  const location = useLocation();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const { courseId } = useParams<{ courseId: string }>();
  const loginState = useSelector((state: RootState) => state.login);
  const selectedChildCompany = useSelector((state: RootState) => state.parent);
  const [activeTab, setActiveTab] = useState<LmsCourseOverviewTab>("settings");
  const [course, setCourse] = useState<any>(null);
  const [categoryOptions, setCategoryOptions] = useState<DropDownOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const { handleError, errorModal } = useErrorModal();
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const hasConsumedCourseCreationFeedback = useRef(false);
  const preserveEditorValuesOnRefreshRef = useRef(false);
  const revalidatePublicationAfterScormUploadRef = useRef(false);
  const [courseRefreshKey, setCourseRefreshKey] = useState(0);
  const [enrolmentRefreshKey, setEnrolmentRefreshKey] = useState(0);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerVariant, setImageViewerVariant] =
    useState<LmsCourseLanguageVariant | null>(null);
  const [isImageUploadModalOpen, setIsImageUploadModalOpen] = useState(false);
  const [replacementImageFile, setReplacementImageFile] = useState<File | null>(
    null
  );
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isScormUploadModalOpen, setIsScormUploadModalOpen] = useState(false);
  const [replacementScormFile, setReplacementScormFile] = useState<File | null>(
    null
  );
  const [isUploadingScorm, setIsUploadingScorm] = useState(false);
  const [uploadValidationError, setUploadValidationError] =
    useState<CourseUploadValidationTarget | null>(null);
  const [selectedAssetVariant, setSelectedAssetVariant] =
    useState<LmsCourseLanguageVariant | null>(null);
  const [isEnrolmentModalOpen, setIsEnrolmentModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    phase: imageUploadPhase,
    progressPercentage: imageUploadProgressPercentage,
    startUpload: startImageUpload,
    handleUploadProgress: handleImageUploadProgress,
    resetUploadProgress: resetImageUploadProgress,
  } = useFileUploadProgress();
  const {
    phase: scormUploadPhase,
    progressPercentage: scormUploadProgressPercentage,
    startUpload: startScormUpload,
    handleUploadProgress: handleScormUploadProgress,
    resetUploadProgress: resetScormUploadProgress,
  } = useFileUploadProgress();

  const {
    control,
    handleSubmit,
    reset,
    resetField,
    setValue,
    trigger,
    watch,
    clearErrors,
    formState: { dirtyFields, errors, touchedFields, isSubmitted },
  } = useForm<CourseSettingsFormValues>({
    defaultValues: mapCourseToCourseEditorFormValues(null, t),
    mode: "onChange",
    shouldFocusError: false,
  });

  const subdomain = window.location.hostname.split(".")[0];
  const shouldBlockLms = shouldBlockLmsForSubdomain({
    subdomain,
    permissions: loginState.permissions,
  });
  const effectiveMode = getEffectiveLmsMode({
    subdomain,
    permissions: loginState.permissions,
  });
  const canUpdateCourse = loginState.permissions.includes(
    COURSE_UPDATE_PERMISSION
  );
  const canUpdateCourseVariants = loginState.permissions.includes(
    COURSE_VARIANT_UPDATE_PERMISSION
  );
  const canViewEntityTranslation = loginState.permissions.includes(
    "frontend_view_entity_translation"
  );
  const canDeleteCoursePermission = loginState.permissions.includes(
    COURSE_DELETE_PERMISSION
  );
  const hasFeaturedImageUploadPermission = loginState.permissions.includes(
    COURSE_FEATURED_IMAGE_UPLOAD_PERMISSION
  );
  const hasScormUploadPermission = loginState.permissions.includes(
    COURSE_SCORM_UPLOAD_PERMISSION
  );
  const canUploadFeaturedImage = hasFeaturedImageUploadPermission;
  const canUploadScorm = hasScormUploadPermission;
  const canAdministerVariantAssets = canUploadFeaturedImage || canUploadScorm;
  const canEditCourseSettings = canUpdateCourse || canUpdateCourseVariants;
  const canPreviewScorm = effectiveMode === "administrator" && loginState.permissions.includes("view_courselanguagevariant");
  const handlePreviewScorm = (variant: LmsCourseLanguageVariant) => {
    if (!canPreviewScorm || !variant.id) return;
    window.open(`/kaerdemy/administrator/courseVariants/${encodeURIComponent(variant.id)}/preview`, "_blank", "noopener,noreferrer");
  };
  const canAdministerCourse =
    canPreviewScorm ||
    canEditCourseSettings ||
    canAdministerVariantAssets ||
    canViewEntityTranslation;
  const isReplacingScormFile = hasLmsCourseVariantScormPackage(selectedAssetVariant);
  const isReplacingFeaturedImage = Boolean(
    (selectedAssetVariant?.featured_image_display_name || selectedAssetVariant?.basename_of_featured_image)
      && selectedAssetVariant?.featured_image_view_link
  );

  if (shouldBlockLms) {
    return <Navigate to="/" replace />;
  }

  const formValues = watch();
  const persistedCourseVariants: LmsCourseLanguageVariant[] =
    course?.variants ?? [];
  const canCreateEnrolments = isLmsCourseEnrolmentEligible(course);
  const certificateExpirable = Boolean(formValues.certificateExpirable);
  const unsavedChangeCount = countLmsCourseEditorDirtyFields(dirtyFields, {
    includeSharedFields: canUpdateCourse,
    includeVariantFields: canUpdateCourseVariants,
  });
  const hasUnsavedChanges = unsavedChangeCount > 0;
  const hasUnsavedChangesRef = useRef(false);
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  const validationFieldOrder = useMemo<CourseEditorValidationField[]>(
    () => [
      ...(canUpdateCourse
        ? ([
            "category",
            "completionIntervalInDays",
            "renewalCooldownInDays",
            "validityIntervalInDays",
          ] as CourseEditorValidationField[])
        : []),
      ...(canUpdateCourseVariants
        ? formValues.variants.flatMap((_variant, variantIndex) => [
            `variants.${variantIndex}.durationInMinutes` as const,
            `variants.${variantIndex}.isActive` as const,
          ])
        : []),
    ],
    [canUpdateCourse, canUpdateCourseVariants, formValues.variants.length]
  );
  const { focusFirstInvalidField, registerValidationTarget } =
    useFocusFirstInvalidField<
      LmsCourseEditorFormValues,
      CourseUploadValidationTarget
    >({
      fieldOrder: validationFieldOrder,
    });

  useConfirmOnLeave(
    canEditCourseSettings && hasUnsavedChanges,
    "unsaved_formChanges_confirm"
  );

  useEffect(() => {
    if (!certificateExpirable) {
      clearErrors("validityIntervalInDays");
      if (formValues.validityIntervalInDays !== "") {
        setValue("validityIntervalInDays", "", {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    }
  }, [
    certificateExpirable,
    clearErrors,
    formValues.validityIntervalInDays,
    setValue,
  ]);

  useEffect(() => {
    if (
      isLoading ||
      !canUpdateCourseVariants ||
      !revalidatePublicationAfterScormUploadRef.current
    ) {
      return;
    }

    revalidatePublicationAfterScormUploadRef.current = false;
    void trigger(
      formValues.variants.map(
        (_variant, variantIndex) => `variants.${variantIndex}.isActive` as const
      )
    );
  }, [
    canUpdateCourseVariants,
    course,
    formValues.variants.length,
    isLoading,
    trigger,
  ]);

  const getFieldErrorMessage = (fieldName: keyof CourseSettingsFormValues) =>
    errors[fieldName] && (touchedFields[fieldName] || isSubmitted)
      ? String(errors[fieldName]?.message ?? "")
      : "";

  const resetNotification = () => {
    setNotificationMessage("");
  };

  useEffect(() => {
    const locationState =
      location.state as LmsCourseOverviewLocationState | null;
    const courseCreationFeedback = locationState?.courseCreationFeedback;

    if (!courseCreationFeedback || hasConsumedCourseCreationFeedback.current) {
      return;
    }

    hasConsumedCourseCreationFeedback.current = true;

    if (courseCreationFeedback.uploadErrors.length === 0) {
      setNotificationMessage(t("course_created_successfully"));
      setNotificationVariant("success");
    } else {
      const uploadErrors = courseCreationFeedback.uploadErrors.reduce<
        Record<string, unknown>
      >((errorsByUpload, uploadError) => {
        const messageKey =
          uploadError.uploadType === "featuredImage"
            ? "course_created_featured_image_failed"
            : "course_created_scorm_failed";

        errorsByUpload[t(messageKey)] =
          uploadError.error ?? t("error_occurred");
        return errorsByUpload;
      }, {});

      handleError(uploadErrors);
    }

    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: null,
    });
  }, [
    handleError,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
    t,
  ]);

  const fetchCourseEnrolmentCount = async (
    extraParameters: Record<string, string | number> = {}
  ) => {
    if (!courseId) {
      return 0;
    }

    const response = await createPromiseRequest({
      ...getLmsManagerRequestConfig(
        effectiveMode,
        selectedChildCompany.subjectId
      ),
      entity: "enrolment",
      method: "get",
      parametersToRender: {
        course_id: courseId,
        limit: 1,
        ...extraParameters,
        ...getLmsManagerRequestParameters(
          effectiveMode,
          selectedChildCompany.subjectId
        ),
      },
    });

    return Number(response?.data?.count ?? 0);
  };

  const loadCourseData = async () => {
    if (!courseId) {
      return;
    }

    setIsLoading(true);

    try {
      const [courseResponse, categoryResponse] = await Promise.all([
        createPromiseRequest({
          ...getLmsManagerRequestConfig(
            effectiveMode,
            selectedChildCompany.subjectId
          ),
          entity: "course",
          entityId: courseId,
          method: "get",
          parametersToRender: {
            ...getLmsManagerRequestParameters(
              effectiveMode,
              selectedChildCompany.subjectId
            ),
            show_translation: true,
          },
        }),
        canUpdateCourse ? loadLmsCourseCategoryOptions() : Promise.resolve([]),
      ]);

      const courseData = courseResponse?.data;
      const courseFormValues = mapCourseToCourseEditorFormValues(courseData, t);
      const courseContent = mapCourseToCourseContentValues(courseData);
      setCourse(
        courseData
          ? {
              ...courseData,
              ...courseContent,
            }
          : courseData
      );
      setCategoryOptions(categoryResponse);
      if (
        !preserveEditorValuesOnRefreshRef.current &&
        !hasUnsavedChangesRef.current
      ) {
        reset(courseFormValues);
      }
    } catch (requestError) {
      handleError(requestError);
    } finally {
      preserveEditorValuesOnRefreshRef.current = false;
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCourseData();
  }, [
    courseId,
    courseRefreshKey,
    effectiveMode,
    reset,
    selectedChildCompany.subjectId,
    canUpdateCourse,
    t,
  ]);

  const resetSavedSharedSettings = (
    values: LmsCourseSharedSettingsFormValues
  ) => {
    LMS_COURSE_SHARED_FIELD_NAMES.forEach((fieldName) => {
      resetField(fieldName, { defaultValue: values[fieldName] });
    });
  };

  const resetSavedVariant = (
    variantIndex: number,
    values: LmsCourseEditorFormValues["variants"][number]
  ) => {
    resetField(`variants.${variantIndex}.durationInMinutes`, {
      defaultValue: values.durationInMinutes,
    });
    resetField(`variants.${variantIndex}.isActive`, {
      defaultValue: values.isActive,
    });
  };

  const handleSaveSettings = async (values: CourseSettingsFormValues) => {
    if (!courseId || !canEditCourseSettings) {
      return;
    }

    const shouldSaveSharedSettings =
      canUpdateCourse && isLmsCourseSharedSettingsDirty(dirtyFields);
    const dirtyVariantIndexes = canUpdateCourseVariants
      ? getDirtyLmsCourseVariantIndexes(dirtyFields)
      : [];
    const requestCount =
      dirtyVariantIndexes.length + (shouldSaveSharedSettings ? 1 : 0);

    if (requestCount === 0) {
      return;
    }

    setIsSaving(true);
    setNotificationMessage("");
    handleError(null);
    const requestErrors: unknown[] = [];
    let successfulRequestCount = 0;
    const isCourseBeingActivated =
      shouldSaveSharedSettings &&
      course?.is_active === false &&
      values.isActive;

    const saveSharedSettings = async () => {
      const sharedSettings = getSharedCourseSettings(values);
      const savedSharedSettings = {
        ...sharedSettings,
        validityIntervalInDays: sharedSettings.certificateExpirable
          ? sharedSettings.validityIntervalInDays
          : "",
      };
      const savedCourseContent: LmsCourseContentValues =
        mapCourseToCourseContentValues(course);

      try {
        await createPromiseRequest({
          entity: "course",
          entityId: courseId,
          method: "put",
          config: {
            headers: {
              "Content-Type": "application/json",
            },
          },
          submitData: buildLmsCourseSharedSubmitData(
            savedSharedSettings,
            savedCourseContent
          ),
        });
        successfulRequestCount += 1;
        resetSavedSharedSettings(savedSharedSettings);
      } catch (requestError) {
        requestErrors.push(requestError);
      }
    };

    const saveVariants = async () => {
      const variantResults = await Promise.allSettled(
        dirtyVariantIndexes.map((variantIndex) => {
          const variantValues = values.variants[variantIndex];

          if (!variantValues?.id) {
            return Promise.reject(new Error("Missing course variant ID"));
          }

          return createPromiseRequest({
            entity: "courseLanguageVariant",
            entityId: variantValues.id,
            method: "put",
            config: {
              headers: {
                "Content-Type": "application/json",
              },
            },
            submitData: buildLmsCourseVariantSubmitData(
              variantValues,
              courseId
            ),
          });
        })
      );

      variantResults.forEach((result, resultIndex) => {
        const variantIndex = dirtyVariantIndexes[resultIndex];

        if (result.status === "fulfilled") {
          successfulRequestCount += 1;
          resetSavedVariant(variantIndex, values.variants[variantIndex]);
          return;
        }

        requestErrors.push(result.reason);
      });
    };

    try {
      if (isCourseBeingActivated) {
        await saveSharedSettings();
      }

      await saveVariants();

      if (shouldSaveSharedSettings && !isCourseBeingActivated) {
        await saveSharedSettings();
      }

      if (successfulRequestCount > 0) {
        hasUnsavedChangesRef.current = requestErrors.length > 0;
        setCourseRefreshKey((currentValue) => currentValue + 1);
      }

      if (requestErrors.length === 0) {
        setNotificationMessage(t("course_changes_saved"));
        setNotificationVariant("success");
      } else {
        if (successfulRequestCount > 0) {
          setNotificationMessage(t("course_changes_partially_saved"));
          setNotificationVariant("warning");
        }
        handleError(requestErrors[0]);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleInvalidFormSubmit = (
    fieldErrors: FieldErrors<LmsCourseEditorFormValues>
  ) => {
    focusFirstInvalidField(fieldErrors);
  };

  const discardUnsavedChanges = useCallback(() => {
    reset();
  }, [reset]);

  const confirmDiscardUnsavedChanges = useCallback(async () => {
    if (!hasUnsavedChanges) {
      return true;
    }

    return showConfirmationModalAsync(t("unsaved_formChanges_confirm"));
  }, [hasUnsavedChanges, showConfirmationModalAsync, t]);

  const handleCancelChanges = async () => {
    if (!hasUnsavedChanges) {
      return;
    }

    const isConfirmed = await confirmDiscardUnsavedChanges();

    if (!isConfirmed) {
      return;
    }

    discardUnsavedChanges();
  };

  const handleTabChange = async (
    _event: SyntheticEvent,
    newValue: LmsCourseOverviewTab
  ) => {
    if (newValue === activeTab) {
      return;
    }

    const isLeavingDirtySettings =
      canEditCourseSettings &&
      activeTab === "settings" &&
      newValue !== "settings" &&
      hasUnsavedChanges;

    if (isLeavingDirtySettings) {
      const isConfirmed = await confirmDiscardUnsavedChanges();

      if (!isConfirmed) {
        return;
      }

      discardUnsavedChanges();
    }

    setActiveTab(newValue);
  };

  const handleUploadReplacementImage = async () => {
    if (!replacementImageFile) {
      setUploadValidationError("replacementImageFile");
      focusFirstInvalidField({}, ["replacementImageFile"]);
      return;
    }

    if (!courseId || !selectedAssetVariant?.id || !canUploadFeaturedImage) {
      return;
    }

    setIsUploadingImage(true);
    startImageUpload();

    try {
      const formData = new FormData();
      formData.append("file", replacementImageFile);
      formData.append("course_language_variant_id", selectedAssetVariant.id);

      await createPromiseRequest({
        entity: "courseLanguageVariant",
        method: "post",
        disableGlobalSpinner: true,
        config: {
          onUploadProgress: handleImageUploadProgress,
        },
        additionalRouteParts: {
          upload_featured_image: "/",
        },
        submitData: formData,
      });

      setIsImageUploadModalOpen(false);
      setReplacementImageFile(null);
      setSelectedAssetVariant(null);
      setNotificationMessage(t("featured_image_updated"));
      setNotificationVariant("success");
      preserveEditorValuesOnRefreshRef.current = true;
      setCourseRefreshKey((currentValue) => currentValue + 1);
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setIsUploadingImage(false);
      resetImageUploadProgress();
    }
  };

  const handleEnrolmentCreated = () => {
    setIsEnrolmentModalOpen(false);
    setNotificationMessage(t("users_enrolled_successfully"));
    setNotificationVariant("success");
    setEnrolmentRefreshKey((currentValue) => currentValue + 1);
  };

  const handleUploadReplacementScorm = async () => {
    if (!replacementScormFile) {
      setUploadValidationError("replacementScormFile");
      focusFirstInvalidField({}, ["replacementScormFile"]);
      return;
    }

    if (!courseId || !selectedAssetVariant?.id || !canUploadScorm) {
      return;
    }

    let unfinishedProgressCount = 0;

    try {
      unfinishedProgressCount = await fetchCourseEnrolmentCount({
        progress_percentage__gt: 0,
        progress_percentage__lt: 100,
      });
    } catch (requestError) {
      handleError(requestError);
      return;
    }

    const confirmMessage =
      unfinishedProgressCount > 0
        ? t("lms_replace_scorm_confirm_message_with_progress", {
            count: unfinishedProgressCount,
          })
        : t("lms_replace_scorm_confirm_message");

    const isConfirmed = await showConfirmationModalAsync(confirmMessage, {
      title: t("lms_replace_scorm_confirm_title"),
      confirmButtonText: t("upload"),
    });

    if (!isConfirmed) {
      return;
    }

    setIsUploadingScorm(true);
    startScormUpload();

    try {
      const formData = new FormData();
      formData.append("file", replacementScormFile);
      formData.append("course_language_variant_id", selectedAssetVariant.id);

      await createPromiseRequest({
        entity: "courseLanguageVariant",
        method: "post",
        config: {
          onUploadProgress: handleScormUploadProgress,
        },
        disableGlobalSpinner: true,
        additionalRouteParts: {
          upload_scorm_file: "/",
        },
        submitData: formData,
      });

      setIsScormUploadModalOpen(false);
      setReplacementScormFile(null);
      setSelectedAssetVariant(null);
      setNotificationMessage(t("scorm_file_updated"));
      setNotificationVariant("success");
      preserveEditorValuesOnRefreshRef.current = true;
      revalidatePublicationAfterScormUploadRef.current = true;
      setCourseRefreshKey((currentValue) => currentValue + 1);
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setIsUploadingScorm(false);
      resetScormUploadProgress();
    }
  };

  const imageUploadContextUnavailable =
    !courseId || !selectedAssetVariant?.id || !canUploadFeaturedImage;
  const scormUploadContextUnavailable =
    !courseId || !selectedAssetVariant?.id || !canUploadScorm;
  const {
    handleSubmit: handleImageUploadSubmit,
    isSubmitting: isSubmittingImageUpload,
  } = useFormSubmission({
    onSubmit: handleUploadReplacementImage,
    disabled: imageUploadContextUnavailable || isUploadingImage,
  });
  const {
    handleSubmit: handleScormUploadSubmit,
    isSubmitting: isSubmittingScormUpload,
  } = useFormSubmission({
    onSubmit: handleUploadReplacementScorm,
    disabled: scormUploadContextUnavailable || isUploadingScorm,
  });

  const handleDownloadScormFile = (variant: LmsCourseLanguageVariant) => {
    if (!variant.scorm_file_download_link) {
      return;
    }

    window.location.href = `/api/v1${variant.scorm_file_download_link}`;
  };

  const handleDownloadFeaturedImage = (variant: LmsCourseLanguageVariant) => {
    const displayName =
      variant.featured_image_display_name ?? variant.basename_of_featured_image;
    if (!displayName || !variant.featured_image_view_link) {
      return;
    }

    const link = document.createElement("a");
    link.href = getDownloadUrl(getLmsCourseImageViewLink(variant.featured_image_view_link, variant.featured_image_url));
    link.download = displayName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleEditTranslations = () => {
    if (!courseId || !canViewEntityTranslation) {
      return;
    }

    navigate(`/course/${courseId}/translation`);
  };

  const persistCourseIsActive = async (nextIsActive: boolean) => {
    if (!courseId || !course || !canUpdateCourse || isUpdatingStatus) {
      return;
    }

    const persistedIsActive = course.is_active !== false;
    if (nextIsActive === persistedIsActive) {
      return;
    }

    setIsUpdatingStatus(true);
    setNotificationMessage("");
    handleError(null);

    try {
      await createPromiseRequest({
        entity: "course",
        entityId: courseId,
        method: "put",
        config: {
          headers: {
            "Content-Type": "application/json",
          },
        },
        submitData: buildLmsCourseSharedSubmitData(
          mapCourseToSharedCourseFormValues(course),
          mapCourseToCourseContentValues(course),
          nextIsActive
        ),
      });

      setCourse((currentCourse: any) => ({
        ...currentCourse,
        is_active: nextIsActive,
        variants: nextIsActive
          ? currentCourse?.variants
          : currentCourse?.variants?.map(
              (variant: LmsCourseLanguageVariant) => ({
                ...variant,
                is_active: false,
              })
            ),
      }));
      setValue("isActive", nextIsActive, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });

      if (!nextIsActive) {
        formValues.variants.forEach((_variant, variantIndex) => {
          resetField(`variants.${variantIndex}.isActive`, {
            defaultValue: false,
          });
        });
      }

      preserveEditorValuesOnRefreshRef.current = true;
      setNotificationMessage(t("course_status_updated"));
      setNotificationVariant("success");
      setCourseRefreshKey((currentValue) => currentValue + 1);
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleStatusChange = async (nextStatus: LmsCourseStatusOption) => {
    if (!courseId || !course || !canUpdateCourse || isUpdatingStatus) {
      return;
    }

    const nextIsActive = nextStatus.value === "active";
    const persistedIsActive = course.is_active !== false;

    if (nextIsActive === persistedIsActive) {
      return;
    }

    const isConfirmed = await showConfirmationModalAsync(
      t("change_status_to_confirm", {
        title: course.title ?? course.name ?? t("courses"),
        status: nextStatus.label,
      }),
      {
        title: t("status_change"),
        confirmButtonText: t("change_status_to", {
          status: nextStatus.label,
        }),
      }
    );

    if (!isConfirmed) {
      return;
    }

    await persistCourseIsActive(nextIsActive);
  };

  const canDeleteCourse =
    canDeleteCoursePermission &&
    !isLoading &&
    !isUpdatingStatus &&
    Boolean(courseId);

  const handleDeleteCourse = async () => {
    if (!courseId || !canDeleteCourse || isDeleting) {
      return;
    }

    let currentEnrolmentCount = 0;

    try {
      currentEnrolmentCount = await fetchCourseEnrolmentCount();
    } catch (requestError) {
      handleError(requestError);
      return;
    }

    if (currentEnrolmentCount > 0) {
      const blockedMessage = t("lms_delete_course_blocked_has_enrolments", {
        count: currentEnrolmentCount,
      });
      const canSetInactive = canUpdateCourse && course?.is_active !== false;

      if (!canSetInactive) {
        const explainOnlyMessage =
          course?.is_active === false
            ? `${blockedMessage} ${t(
                "lms_delete_course_blocked_already_inactive"
              )}`
            : blockedMessage;

        await showConfirmationModalAsync(explainOnlyMessage, {
          title: t("lms_delete_course_blocked_title"),
          confirmButtonText: t("ok_capital"),
          disableCancelButton: true,
        });
        return;
      }

      const shouldSetInactive = await showConfirmationModalAsync(
        `${blockedMessage} ${t(
          "lms_delete_course_blocked_set_inactive_description"
        )}`,
        {
          title: t("lms_delete_course_blocked_title"),
          confirmButtonText: t("lms_delete_course_set_inactive"),
        }
      );

      if (shouldSetInactive) {
        await persistCourseIsActive(false);
      }

      return;
    }

    const isConfirmed = await showConfirmationModalAsync(
      t("lms_delete_course_confirm_message"),
      {
        title: t("lms_delete_course_confirm_title"),
        confirmButtonText: t("delete"),
      }
    );

    if (!isConfirmed) {
      return;
    }

    setIsDeleting(true);

    try {
      await createPromiseRequest({
        entity: "course",
        entityId: courseId,
        method: "delete",
      });
      navigate(getLmsCoursesRoute(effectiveMode));
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setIsDeleting(false);
    }
  };

  const courseContent = mapCourseToCourseContentValues(course);
  const featuredImage = getLmsCourseFeaturedImage(course);

  const featuredImageUrl = featuredImage?.viewLink
    ? getImageUrl(
        featuredImage.viewLink,
        selectedChildCompany.subjectId
          ? String(selectedChildCompany.subjectId)
          : undefined
      )
    : null;

  const featuredImageViewerImages =
    featuredImage?.viewLink && courseId
      ? [
          {
            id: featuredImage.id || courseId,
            name: featuredImage.displayName,
            view_link: featuredImage.viewLink,
            download_link: featuredImage.viewLink,
            extension: featuredImage.displayName.split(".").pop() || "png",
          },
        ]
      : [];

  const imageViewerVariantDisplayName =
    imageViewerVariant?.featured_image_display_name ??
    imageViewerVariant?.basename_of_featured_image;
  const imageViewerVariantViewLink = imageViewerVariant?.featured_image_view_link
    ? getLmsCourseImageViewLink(imageViewerVariant.featured_image_view_link, imageViewerVariant.featured_image_url)
    : null;
  const variantImageViewerImages =
    imageViewerVariant?.id &&
    imageViewerVariantViewLink &&
    imageViewerVariantDisplayName
      ? [
          {
            id: imageViewerVariant.id,
            name: imageViewerVariantDisplayName,
            view_link: imageViewerVariantViewLink,
            download_link: imageViewerVariantViewLink,
            extension: imageViewerVariantDisplayName.split(".").pop() || "png",
          },
        ]
      : [];
  const imageViewerImages = imageViewerVariant
    ? variantImageViewerImages
    : featuredImageViewerImages;

  const editorVariants = formValues.variants
    .map((variantValues) =>
      persistedCourseVariants.find(
        (variant: LmsCourseLanguageVariant) =>
          String(variant.id ?? "") === variantValues.id
      )
    )
    .filter((variant): variant is LmsCourseLanguageVariant => Boolean(variant));

  const handleEnrolmentTableRefreshed = useCallback(() => {
    setEnrolmentRefreshKey((currentValue) => currentValue + 1);
  }, []);

  if (!courseId) {
    return <Navigate to={getLmsCoursesRoute(effectiveMode)} replace />;
  }

  if (requiredMode && effectiveMode !== requiredMode) {
    return <Navigate to={getLmsCoursesRoute(effectiveMode)} replace />;
  }

  const deleteCourseButton = (
    <GenericButton
      color="error"
      variant="outlined"
      onClick={handleDeleteCourse}
      disabled={!canDeleteCourse || isDeleting}
      waitingForResponse
      showSpinnerOnDisable
      startIcon={<DeleteForeverIcon />}
    >
      {t("delete_course")}
    </GenericButton>
  );

  return (
    <>
      <Grid container className={styles.mainGrid}>
        <Grid item xs={12}>
          <div className={styles.courseOverviewNav}>
            <LmsBackButton />
          </div>
          <RoundedContentBox>
            <LmsCourseOverviewHeader
              featuredImageUrl={featuredImageUrl}
              imageAlt={
                featuredImage?.displayName ||
                course?.title ||
                course?.name ||
                t("course_featured_image")
              }
              title={course?.title ?? course?.name ?? t("overview")}
              onViewImage={() => {
                setImageViewerVariant(null);
                setImageViewerOpen(true);
              }}
              canViewImage={Boolean(featuredImage?.viewLink)}
              topRightContent={
                canUpdateCourse ? (
                  <LmsCourseStatusDropDown
                    status={
                      course
                        ? course.is_active === false
                          ? "inactive"
                          : "active"
                        : null
                    }
                    disabled={isLoading || isSaving}
                    isUpdating={isUpdatingStatus}
                    onStatusChange={handleStatusChange}
                  />
                ) : undefined
              }
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                className={styles.tabsContainer}
                variant="scrollable"
                scrollButtons={false}
                allowScrollButtonsMobile
                sx={{
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#8c1ec8",
                  },
                  "& .MuiTab-root": {
                    minWidth: "auto",
                    flexShrink: 0,
                    fontSize: {
                      xs: "0.75rem",
                      sm: "0.8125rem",
                      md: "0.875rem",
                    },
                    padding: { xs: "0.75rem 0.625rem", sm: "0.75rem 1rem" },
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "#8c1ec8",
                  },
                }}
              >
                <Tab
                  value="settings"
                  label={t(canAdministerCourse ? "settings" : "overview")}
                  data-testid={
                    canAdministerCourse
                      ? "lms-admin-course-settings-tab"
                      : "lms-admin-course-overview-tab"
                  }
                />
                <Tab
                  value="statistics"
                  label={t("statistics")}
                  data-testid="lms-admin-course-statistics-tab"
                />
                <Tab
                  value="enrolments"
                  data-testid="lms-admin-course-enrolments-tab"
                  label={t("enrolments")}
                />
              </Tabs>
            </LmsCourseOverviewHeader>
          </RoundedContentBox>
        </Grid>

        {canUpdateCourse && (
          <Grid
            item
            xs={12}
            className={
              activeTab !== "settings" ? styles.tabPanelHidden : undefined
            }
            aria-hidden={activeTab !== "settings"}
          >
            {isLoading ? (
              <RoundedContentBox>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  py={6}
                >
                  <Typography>{t("loading")}</Typography>
                </Box>
              </RoundedContentBox>
            ) : (
              <div className={styles.summaryGrid}>
                {[
                  {
                    label: t("created_on"),
                    value: formatDateValue(course?.created_on),
                  },
                  {
                    label: t("created_by"),
                    value: course?.created_by?.name ?? "-",
                  },
                  {
                    label: t("last_modified_on"),
                    value: formatDateValue(course?.modified_on),
                  },
                  {
                    label: t("last_modified_by"),
                    value: course?.modified_by?.name ?? "-",
                  },
                ].map((item) => (
                  <div key={item.label} className={styles.summaryCard}>
                    <div className={styles.summaryLabel}>{item.label}</div>
                    <div className={styles.summaryValue}>{item.value}</div>
                  </div>
                ))}
              </div>
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <form
            id={settingsFormId}
            noValidate
            onSubmit={handleSubmit(handleSaveSettings, handleInvalidFormSubmit)}
            aria-hidden={activeTab !== "settings"}
            className={`
              ${styles.settingsTabContentSection}
              ${activeTab !== "settings" ? styles.tabPanelHidden : ""}
            `}
          >
            {canAdministerCourse ? (
              <>
                <LmsCourseContentSection
                  canEditTranslations={canViewEntityTranslation}
                  content={courseContent}
                  onEditTranslations={handleEditTranslations}
                  t={t}
                />
                {canUpdateCourse && (
                  <LmsCourseSharedSettingsForm
                    categoryOptions={categoryOptions}
                    certificateExpirable={certificateExpirable}
                    control={control}
                    disabled={isSaving || isUpdatingStatus}
                    errors={errors}
                    getFieldErrorMessage={getFieldErrorMessage}
                    registerValidationTarget={registerValidationTarget}
                    t={t}
                  />
                )}
                <LmsCourseVariantsSection
                  canEditVariants={canUpdateCourseVariants}
                  canUploadFeaturedImage={canUploadFeaturedImage}
                  canUploadScorm={canUploadScorm}
                  control={control}
                  courseIsActive={formValues.isActive}
                  disabled={isSaving || isUpdatingStatus}
                  errors={errors}
                  onDownloadFeaturedImage={handleDownloadFeaturedImage}
                  onDownloadScorm={handleDownloadScormFile}
                  onPreviewScorm={canPreviewScorm ? handlePreviewScorm : undefined}
                  onUploadFeaturedImage={(variant) => {
                    setSelectedAssetVariant(variant);
                    setUploadValidationError(null);
                    resetImageUploadProgress();
                    setIsImageUploadModalOpen(true);
                  }}
                  onUploadScorm={(variant) => {
                    setSelectedAssetVariant(variant);
                    setUploadValidationError(null);
                    setIsScormUploadModalOpen(true);
                  }}
                  onViewFeaturedImage={(variant) => {
                    setImageViewerVariant(variant);
                    setImageViewerOpen(true);
                  }}
                  registerValidationTarget={registerValidationTarget}
                  t={t}
                  variants={editorVariants}
                />
              </>
            ) : (
              <LmsCourseOverviewReadOnly
                requiredMode="administrator"
                subdomain="manage"
                renderOnlyOverview
                courseData={course}
                isCourseLoading={isLoading}
              />
            )}

            <LmsCourseOverviewDangerZone
              canDeleteCourse={canDeleteCoursePermission}
              deleteCourseButton={deleteCourseButton}
              isLoading={isLoading}
              t={t}
            />
          </form>

          <LmsCourseOverviewTabPanels
            activeTab={activeTab}
            courseId={courseId}
            enrolmentRefreshKey={enrolmentRefreshKey}
            mode={effectiveMode}
            onCreateEnrolment={() => setIsEnrolmentModalOpen(true)}
            onEnrolmentTableRefreshed={handleEnrolmentTableRefreshed}
            onError={handleError}
            canCreateEnrolments={canCreateEnrolments}
            createEnrolmentsDisabledMessage={t(
              "course_enrolment_requires_scorm"
            )}
          />
        </Grid>
      </Grid>

      <Slide
        direction="up"
        in={canEditCourseSettings && activeTab === "settings"}
        mountOnEnter
        unmountOnExit
      >
        <Box className={styles.floatingActionBar}>
          <Box className={styles.floatingActionLeft}>
            <GenericButton
              variant="outlined"
              onClick={handleCancelChanges}
              disabled={isSaving || isUpdatingStatus || !hasUnsavedChanges}
            >
              {t("cancel_changes")}
            </GenericButton>
          </Box>
          <Box className={styles.floatingActionRight}>
            <GenericButton
              type="submit"
              form={settingsFormId}
              data-testid="lms-course-save-button"
              disabled={
                !canEditCourseSettings ||
                isSaving ||
                isUpdatingStatus ||
                !hasUnsavedChanges
              }
            >
              {t("save_changes_button")}
            </GenericButton>
          </Box>
        </Box>
      </Slide>

      <Dialog
        data-testid="lms-replace-scorm-dialog"
        open={canUploadScorm && isScormUploadModalOpen}
        onClose={() => {
          if (!isUploadingScorm) {
            setIsScormUploadModalOpen(false);
            setReplacementScormFile(null);
            setSelectedAssetVariant(null);
            setUploadValidationError(null);
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle>{t(isReplacingScormFile ? "replace_scorm_file" : "upload_scorm_file")}</DialogTitle>
        <form
          style={{ display: "contents" }}
          onSubmit={handleScormUploadSubmit}
        >
          <DialogContent className={styles.dialogContent}>
            <LmsFileDropzone
              inputDataTestId="lms-replacement-scorm-input"
              validationTargetRef={registerValidationTarget(
                "replacementScormFile"
              )}
              icon={<FolderZipOutlinedIcon className={styles.dropzoneIcon} />}
              value={replacementScormFile}
              onChange={(file) => {
                setReplacementScormFile(file);
                setUploadValidationError(null);
                resetScormUploadProgress();
              }}
              disabled={isUploadingScorm}
              accept={{
                "application/zip": [".zip"],
                "application/x-zip-compressed": [".zip"],
              }}
              acceptedFileTypesText={t("accepted_file_types", {
                fileTypes: ".zip",
              })}
              emptyStateText={t("drop_scorm_file_here")}
              helperText={t(isReplacingScormFile ? "lms_replace_scorm_file_helper" : "lms_upload_scorm_file_helper")}
              rejectionMessage={t("only_zip_files_allowed")}
              errorMessage={
                uploadValidationError === "replacementScormFile"
                  ? t("field_required")
                  : undefined
              }
            />
            <FileUploadProgress
              phase={scormUploadPhase}
              progressPercentage={scormUploadProgressPercentage}
              uploadingTitle={t("scorm_upload_in_progress")}
              uploadingDescription={t("scorm_upload_in_progress_description")}
              processingTitle={t("scorm_upload_processing")}
              processingDescription={t("scorm_upload_processing_description")}
              dataTestId="lms-replace-scorm-upload-progress"
              className={styles.scormUploadProgress}
            />
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <GenericButton
              variant="outlined"
              onClick={() => {
                setIsScormUploadModalOpen(false);
                setReplacementScormFile(null);
                setSelectedAssetVariant(null);
                setUploadValidationError(null);
              }}
              disabled={isUploadingScorm}
            >
              {t("cancel")}
            </GenericButton>
            <GenericButton
              type="submit"
              data-testid="lms-replace-scorm-submit-button"
              disabled={
                isUploadingScorm ||
                isSubmittingScormUpload ||
                scormUploadContextUnavailable
              }
            >
              {t("upload")}
            </GenericButton>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        data-testid="lms-replace-featured-image-dialog"
        open={canUploadFeaturedImage && isImageUploadModalOpen}
        onClose={() => {
          if (!isUploadingImage) {
            setIsImageUploadModalOpen(false);
            setReplacementImageFile(null);
            setSelectedAssetVariant(null);
            setUploadValidationError(null);
            resetImageUploadProgress();
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle>{t(isReplacingFeaturedImage ? "replace_featured_image" : "upload_featured_image")}</DialogTitle>
        <form
          style={{ display: "contents" }}
          onSubmit={handleImageUploadSubmit}
        >
          <DialogContent className={styles.dialogContent}>
            <LmsFileDropzone
              inputDataTestId="lms-replacement-featured-image-input"
              validationTargetRef={registerValidationTarget(
                "replacementImageFile"
              )}
              icon={<InsertPhotoOutlinedIcon className={styles.dropzoneIcon} />}
              value={replacementImageFile}
              onChange={(file) => {
                setReplacementImageFile(file);
                setUploadValidationError(null);
                resetImageUploadProgress();
              }}
              disabled={isUploadingImage}
              accept={{
                "image/jpeg": [".jpg", ".jpeg"],
                "image/png": [".png"],
                "image/webp": [".webp"],
              }}
              acceptedFileTypesText={t("accepted_file_types", {
                fileTypes: ".jpg, .jpeg, .png, .webp",
              })}
              emptyStateText={t("drop_featured_image_here")}
              helperText={t("course_featured_image_helper")}
              rejectionMessage={t("only_image_files_allowed")}
              errorMessage={
                uploadValidationError === "replacementImageFile"
                  ? t("field_required")
                  : undefined
              }
            />
            <FileUploadProgress
              phase={imageUploadPhase}
              progressPercentage={imageUploadProgressPercentage}
              dataTestId="lms-replace-featured-image-upload-progress"
            />
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <GenericButton
              variant="outlined"
              onClick={() => {
                setIsImageUploadModalOpen(false);
                setReplacementImageFile(null);
                setSelectedAssetVariant(null);
                setUploadValidationError(null);
                resetImageUploadProgress();
              }}
              disabled={isUploadingImage}
            >
              {t("cancel")}
            </GenericButton>
            <GenericButton
              type="submit"
              data-testid="lms-replace-featured-image-submit-button"
              disabled={
                isUploadingImage ||
                isSubmittingImageUpload ||
                imageUploadContextUnavailable
              }
            >
              {t("upload")}
            </GenericButton>
          </DialogActions>
        </form>
      </Dialog>

      <LmsEnrolmentCreateModal
        open={isEnrolmentModalOpen}
        courseId={courseId}
        mode={effectiveMode}
        onClose={() => setIsEnrolmentModalOpen(false)}
        onCompleted={handleEnrolmentCreated}
        enableCourseSelection={false}
      />

      {imageViewerImages.length > 0 && (
        <ImageViewer
          open={imageViewerOpen}
          onClose={() => {
            setImageViewerOpen(false);
            setImageViewerVariant(null);
          }}
          images={imageViewerImages}
          initialImageIndex={0}
          doNotShowFileName={subdomain !== "admin"}
        />
      )}

      {notificationMessage && (
        <GenericNotification
          message={notificationMessage}
          variant={notificationVariant}
          handleCloseSnackbar={resetNotification}
        />
      )}

      {errorModal}
    </>
  );
}
